import { Container, Divider, Grid, Stack, TextField } from '@mui/material';
import React, { useState } from 'react';
import { FinsightEntityField } from 'shared';

import { ConstraintsManager } from './components/ConstraintsManager';
import { FieldFlagsSettings } from './components/FieldFlagsSettings';
import { FieldPreview } from './components/FieldPreview';
import { FieldTypeSelector } from './components/FieldTypeSelector';
import { InputArea } from './components/InputArea';
import { Meta } from './components/Meta';
import { PropertiesManager } from './components/PropertiesManager';
import { PropertyEditorModal } from './components/PropertyEditorModal';
import { UIAdornmentsSettings } from './components/UIAdornmentsSettings';
import { UIVisibilitySettings } from './components/UIVisibilitySettings';

interface FieldJSONGeneratorProps {
  fieldName: string;
  field: FinsightEntityField['schema'];
  setField: (field: FinsightEntityField['schema']) => void;
}

export function FieldEditor(props: FieldJSONGeneratorProps) {
  const { fieldName, field, setField } = props;
  const [currentEditingPropertyIndex, setCurrentEditingPropertyIndex] =
    useState<number | null>(null);

  const handlePropertyClick = (index: number) => {
    setCurrentEditingPropertyIndex(index);
  };

  const handlePropertyUpdate = (
    updatedProperty: FinsightEntityField['schema'],
  ) => {
    if (
      currentEditingPropertyIndex !== null &&
      Array.isArray(field.properties)
    ) {
      const updatedProperties = [...field.properties];
      updatedProperties[currentEditingPropertyIndex] = updatedProperty;
      setField({
        ...field,
        properties: updatedProperties,
      });
    }
  };

  const handleCloseModal = () => {
    setCurrentEditingPropertyIndex(null);
  };

  const handleDeleteProperty = (index: number) => {
    if (Array.isArray(field.properties)) {
      const updatedProperties = field.properties.filter((_, i) => i !== index);
      setField({
        ...field,
        properties: updatedProperties,
      });
    }
  };

  return (
    <>
      <Container maxWidth="lg">
        <Grid container spacing={2}>
          <Grid item xs={8}>
            <Stack gap={5}>
              <InputArea
                name="Name"
                description="The name is used to uniquely identify this field. (Not shown top level)"
              >
                <TextField
                  fullWidth
                  label="Name"
                  value={field.name}
                  onChange={(e) => setField({ ...field, name: e.target.value })}
                />
              </InputArea>

              <FieldTypeSelector field={field} setField={setField} />

              <PropertiesManager
                field={field}
                setField={setField}
                onPropertyClick={handlePropertyClick}
                onDeleteProperty={handleDeleteProperty}
                disabled={field.type !== 'object' && field.type !== 'enum'}
              />

              <ConstraintsManager
                field={field}
                setField={setField}
                disabled={field.type !== 'entity'}
              />

              <Meta fieldName={fieldName} field={field} setField={setField} />

              <Divider>UI Settings</Divider>

              <UIVisibilitySettings field={field} setField={setField} />

              <UIAdornmentsSettings field={field} setField={setField} />
            </Stack>
          </Grid>
          <Grid item xs={4}>
            <FieldFlagsSettings field={field} setField={setField} />
          </Grid>
        </Grid>

        <Divider sx={{ my: 5 }}>Preview</Divider>

        <FieldPreview field={field} setField={setField} />
      </Container>
      <PropertyEditorModal
        open={currentEditingPropertyIndex !== null}
        onClose={handleCloseModal}
        currentEditingPropertyIndex={currentEditingPropertyIndex}
        field={field}
        onPropertyUpdate={handlePropertyUpdate}
      />
    </>
  );
}
